import React from 'react';
import './FilterInsightsModal.css';
import { X, Filter } from 'lucide-react';

type GovernmentFilter = 'municipal' | 'provincial' | 'regional';

interface FilterInsightsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLevels: GovernmentFilter[];
  selectedMunicipalities: string[];
  selectedProvStreams: string[];
  selectedRegions: string[];
  municipalOptions: string[];
  provincialStreamOptions: string[];
  regionalOptions: string[];
  sortBy: 'created' | 'event_date';
  onToggleLevel: (level: GovernmentFilter) => void;
  onToggleMunicipality: (value: string) => void;
  onToggleProvincialStream: (value: string) => void;
  onToggleRegion: (value: string) => void;
  onSortChange: (sortBy: 'created' | 'event_date') => void;
  onResetFilters: () => void;
  hasActiveFilters: boolean;
}

const FilterInsightsModal: React.FC<FilterInsightsModalProps> = ({
  isOpen,
  onClose,
  selectedLevels,
  selectedMunicipalities,
  selectedProvStreams,
  selectedRegions,
  municipalOptions,
  provincialStreamOptions,
  regionalOptions,
  sortBy,
  onToggleLevel,
  onToggleMunicipality,
  onToggleProvincialStream,
  onToggleRegion,
  onSortChange,
  onResetFilters,
  hasActiveFilters,
}) => {
  if (!isOpen) return null;

  const levelOptions = [
    { id: 'municipal' as GovernmentFilter, label: 'Municipal', hasData: municipalOptions.length > 0 },
    { id: 'regional' as GovernmentFilter, label: 'Regional', hasData: regionalOptions.length > 0 },
    { id: 'provincial' as GovernmentFilter, label: 'Provincial', hasData: provincialStreamOptions.length > 0 },
  ];

  const availableLevels = levelOptions.filter(option => option.hasData);
  const hasAnyLevelData = availableLevels.length > 0;

  return (
    <div className="filter-modal-overlay" onClick={onClose}>
      <div className="filter-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="filter-modal-header">
          <h3>
            <Filter size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Filter Insights
          </h3>
          <button className="filter-modal-close" onClick={onClose} title="Close">
            <X size={20} />
          </button>
        </div>

        <div className="filter-modal-body">
          {/* Sort By Section */}
          <div className="filter-section">
            <span className="filter-label">Sort By</span>
            <div className="filter-chip-group">
              <button
                type="button"
                className={`filter-chip ${sortBy === 'event_date' ? 'active' : ''}`}
                onClick={() => onSortChange('event_date')}
              >
                Event/Meeting Date
              </button>
              <button
                type="button"
                className={`filter-chip ${sortBy === 'created' ? 'active' : ''}`}
                onClick={() => onSortChange('created')}
              >
                Date Created
              </button>
            </div>
          </div>

          {/* Government Level filters */}
          {hasAnyLevelData && (
            <div className="filter-section">
              <span className="filter-label">Government Level</span>
              <div className="filter-chip-group">
                {levelOptions.map(({ id, label, hasData }) => (
                  <button
                    key={id}
                    type="button"
                    className={`filter-chip ${selectedLevels.includes(id) ? 'active' : ''} ${hasData ? '' : 'no-data'}`}
                    onClick={() => onToggleLevel(id)}
                    title={!hasData ? 'No insights from this level yet' : undefined}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Always show Municipalities if data exists */}
          {municipalOptions.length > 0 && (
            <div className="filter-section">
              <span className="filter-label">Municipalities</span>
              <div className="filter-chip-group wrap">
                {municipalOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`filter-chip small ${selectedMunicipalities.includes(option) ? 'active' : ''}`}
                    onClick={() => onToggleMunicipality(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Always show Regions if data exists */}
          {regionalOptions.length > 0 && (
            <div className="filter-section">
              <span className="filter-label">Regions</span>
              <div className="filter-chip-group wrap">
                {regionalOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`filter-chip small ${selectedRegions.includes(option) ? 'active' : ''}`}
                    onClick={() => onToggleRegion(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Always show Ministries if data exists */}
          {provincialStreamOptions.length > 0 && (
            <div className="filter-section">
              <span className="filter-label">Ministries</span>
              <div className="filter-chip-group wrap">
                {provincialStreamOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`filter-chip small ${selectedProvStreams.includes(option) ? 'active' : ''}`}
                    onClick={() => onToggleProvincialStream(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!hasAnyLevelData && (
            <div className="filter-empty-state">
              <p>No filter options available yet. Filter options will appear once insights are loaded.</p>
            </div>
          )}
        </div>

        <div className="filter-modal-footer">
          <button 
            className="filter-reset-btn" 
            onClick={onResetFilters}
            disabled={!hasActiveFilters}
          >
            Reset All Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterInsightsModal;
